// Plan ▸ Mind Maps (the Mac's `RoomPage.maps`): the map in use in this browser on its paper, with the tab's controls —
// the map menu (the project's mind maps, New Mind Map, Rename…, Move to Trash), the zoom (−, the percentage, +,
// Fit) and the Pen (drawing on the paper with a mouse or a finger; localStorage `ws:mapPenOn`) with its ink
// (`ws:mapPenInk`). With no map yet, "Start a mind map". Research shows the same canvas for a map chosen in the contents
// (`MapDocumentPage`, from WritePage), with the zoom in its own head.
import { useState } from 'react';
import { readMapContent, SwiftReader, type OpenedProject, type Project } from '@writing-suite/core';
import { announce } from '../../shell/context.ts';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { useWorkspace } from '../../shell/workspace.ts';
import { locksOf } from '../../store/planModel.ts';
import { useSharedPreference } from '../../store/plan.ts';
import { MAP_PREF, mapInUse, mapsOf, newMindMap, sendMapCommand, useMapPen, useMapZoom, type MapEntry } from '../../store/maps.ts';
import type { LiveProject } from '../../store/project.ts';
import { TextDialog } from '../Margin.tsx';
import { INKS } from '../FormatBar.tsx';
import { Icon } from '../Icons.tsx';
import { ConfirmDialog, MenuButton, type MenuEntry } from '../Menu.tsx';
import { MapCanvas, zoomPercent } from './MapCanvas.tsx';
import { useScappleImport } from './ScappleImport.tsx';

/** Whether the map can be changed here, and if not, the note that says why. */
function mapAccess(project: Project, live: LiveProject | null, fragmentId: string, role: string | undefined): { editable: boolean; note: { kind: string; text: string } | null } {
  const fragment = project.fragment(fragmentId);
  if (!live?.writable) {
    return { editable: false, note: { kind: role === 'commenter' ? 'commenter' : 'true', text: role === 'commenter' ? 'Read only — you can comment on the project’s documents' : 'Read only' } };
  }
  if (fragment?.trashed) return { editable: false, note: { kind: 'true', text: 'In the Trash — read only' } };
  if (locksOf(project).has(fragmentId)) return { editable: false, note: { kind: 'locked', text: 'Locked — unlock it on the shelf to change it' } };
  return { editable: true, note: null };
}

function ReadOnlyNote({ note }: { note: { kind: string; text: string } | null }) {
  return note ? (
    <p className="read-only-note" data-read-only={note.kind}>
      {note.text}
    </p>
  ) : null;
}

/** −, the percentage (Actual Size), +, Fit. */
export function ZoomControls({ fragmentId }: { fragmentId: string | null }) {
  const zoom = useMapZoom(fragmentId);
  const send = (c: Parameters<typeof sendMapCommand>[1]) => fragmentId && sendMapCommand(fragmentId, c);
  return (
    <span className="map-zoom" role="group" aria-label="Zoom">
      <button type="button" className="quiet-button" aria-label="Zoom Out" title="Zoom Out (⌘−)" disabled={!fragmentId} onClick={() => send('out')}>
        −
      </button>
      <button type="button" className="quiet-button map-zoom-value" aria-label={`Zoom ${zoomPercent(zoom)}: Actual Size`} title="Actual Size" disabled={!fragmentId} onClick={() => send('actual')}>
        {zoomPercent(zoom)}
      </button>
      <button type="button" className="quiet-button" aria-label="Zoom In" title="Zoom In (⌘+)" disabled={!fragmentId} onClick={() => send('in')}>
        +
      </button>
      <button type="button" className="quiet-button" title="Zoom to Fit (⌘0)" disabled={!fragmentId} onClick={() => send('fit')}>
        Fit
      </button>
    </span>
  );
}

/** The pen (on: a mouse or a finger draws on the paper too; a pen always draws) and its ink. */
export function PenControls({ disabled }: { disabled: boolean }) {
  const pen = useMapPen();
  const entries: MenuEntry[] = [
    { kind: 'radio', label: 'Charcoal', checked: pen.ink === null, color: 'var(--map-ink)', onSelect: () => pen.setInk(null) },
    ...INKS.map((i): MenuEntry => ({ kind: 'radio', label: i.name, checked: pen.ink === i.ink, color: `var(--ink-${i.ink})`, onSelect: () => pen.setInk(i.ink) })),
  ];
  return (
    <span className="map-pen" role="group" aria-label="Pen">
      <button
        type="button"
        className="quiet-button map-pen-toggle"
        aria-pressed={pen.on && !disabled}
        title={pen.on ? 'Pen: on (draw on the paper)' : 'Pen: off'}
        disabled={disabled}
        onClick={() => {
          pen.setOn(!pen.on);
          announce(pen.on ? 'Pen off' : 'Pen on');
        }}
      >
        <Icon name="pencil" /> Pen
      </button>
      <MenuButton label="Pen Ink" className="quiet-button map-bar-menu map-pen-ink" title="The pen’s ink" disabled={disabled} entries={entries}>
        <span className={`map-ink-swatch${pen.ink ? ` tint-${pen.ink}` : ''}`} aria-hidden="true" />
      </MenuButton>
    </span>
  );
}

function useMaps(props: PageProps) {
  const maps = mapsOf(props.project);
  const [remembered, remember] = useSharedPreference<string | null>(MAP_PREF.inUse(props.projectId), null);
  return { maps, map: mapInUse(maps, remembered), remember };
}

/** New Mind Map: made, chosen, and opened with its centre being typed over. */
function startMap(props: PageProps, remember: (id: string) => void) {
  if (!props.live) return;
  try {
    const made = newMindMap(props.live, props.project);
    remember(made.fragmentID);
    announce('New mind map');
  } catch (err) {
    announce(err instanceof Error ? err.message : String(err));
  }
}

/** Whether the map has a centre (Arrange Around Centre needs one). */
function hasCentre(props: PageProps, fragmentId: string): boolean {
  const doc = props.live?.doc(`fragment.${fragmentId}`) ?? (props.project as OpenedProject).fragmentDoc?.(fragmentId) ?? null;
  return doc ? readMapContent(new SwiftReader(doc)).centre !== null : false;
}

/** The tab's controls: the map menu (the maps, New, Import Scapple Board…, Rename, Trash; then Add to Map…, Link Notes to
 *  Cast…, Arrange Around Centre, Export as PNG…/PDF…, which the canvas carries out) and the zoom. */
export function MapControls(props: PageProps) {
  const { maps, map, remember } = useMaps(props);
  const [renaming, setRenaming] = useState<MapEntry | null>(null);
  const [trashing, setTrashing] = useState<MapEntry | null>(null);
  const writable = !!props.live?.writable;
  const locked = !!map && locksOf(props.project).has(map.fragmentId);
  const scapple = useScappleImport({ live: props.live, projectId: props.projectId, mapsHref: props.hrefFor('plan/maps', null) });
  const entries: MenuEntry[] = [
    ...maps.map((m): MenuEntry => ({ kind: 'radio', label: m.title, checked: m === map, onSelect: () => remember(m.fragmentId) })),
    ...(maps.length ? [{ kind: 'separator' } as MenuEntry] : []),
    { label: 'New Mind Map', disabled: !writable, onSelect: () => startMap(props, remember) },
    { label: 'Import Scapple Board…', disabled: !writable, onSelect: scapple.start },
    { label: 'Rename…', disabled: !writable || !map || locked, onSelect: () => map && setRenaming(map) },
    { label: 'Move to Trash', disabled: !writable || !map || locked, onSelect: () => map && setTrashing(map) },
    { kind: 'separator' },
    { label: 'Add to Map…', disabled: !writable || !map || locked, onSelect: () => map && sendMapCommand(map.fragmentId, 'add') },
    { label: 'Link Notes to Cast…', disabled: !writable || !map || locked, onSelect: () => map && sendMapCommand(map.fragmentId, 'cast') },
    { label: 'Arrange Around Centre', disabled: !writable || !map || locked || !hasCentre(props, map.fragmentId), onSelect: () => map && sendMapCommand(map.fragmentId, 'arrange') },
    { kind: 'separator' },
    { label: 'Export as PNG…', disabled: !map, onSelect: () => map && sendMapCommand(map.fragmentId, 'png') },
    { label: 'Export as PDF…', disabled: !map, onSelect: () => map && sendMapCommand(map.fragmentId, 'pdf') },
  ];
  return (
    <>
      <MenuButton label="Mind map" className="quiet-button header-menu map-menu" title="Choose a mind map, or make one" entries={entries}>
        {map ? map.title : 'Mind Maps'}
      </MenuButton>
      {map ? <ZoomControls fragmentId={map.fragmentId} /> : null}
      {map ? <PenControls disabled={!mapAccess(props.project, props.live, map.fragmentId, undefined).editable} /> : null}
      {scapple.element}
      {renaming ? (
        <TextDialog
          title="Rename Mind Map"
          label="Name"
          initial={renaming.title}
          confirm="Rename"
          onCancel={() => setRenaming(null)}
          onDone={(text) => {
            setRenaming(null);
            try {
              props.live?.edit((e) => e.renameFragment(renaming.fragmentId, text));
            } catch (err) {
              announce(err instanceof Error ? err.message : String(err));
            }
          }}
        />
      ) : null}
      {trashing ? (
        <ConfirmDialog
          title="Move to Trash"
          message={<p>Move “{trashing.title}” to the Trash? It stays there until you empty it, and can be put back from the contents.</p>}
          confirm="Move to Trash"
          onCancel={() => setTrashing(null)}
          onConfirm={() => {
            setTrashing(null);
            try {
              props.live?.edit((e) => e.trash([trashing.nodeId], trashing.arrangementId));
              announce(`“${trashing.title}” moved to the Trash`);
            } catch (err) {
              announce(err instanceof Error ? err.message : String(err));
            }
          }}
        />
      ) : null}
    </>
  );
}

export function MindMapsPage(props: PageProps) {
  const ws = useWorkspace();
  const { map, remember } = useMaps(props);
  if (!map) {
    const writable = !!props.live?.writable;
    return (
      <div className="maps-page maps-empty">
        <header className="page-head">
          <h1 tabIndex={-1}>Mind Maps</h1>
        </header>
        <div className="story-empty" role="note">
          <p className="story-empty-title">Think on paper</p>
          <p>Put notes down anywhere and join them with lines, before the book has a shape. Maps are kept in Research &amp; Notes ▸ Mind Maps.</p>
          <p>
            <button type="button" className="button primary" disabled={!writable} onClick={() => startMap(props, remember)}>
              Start a mind map
            </button>
          </p>
        </div>
      </div>
    );
  }
  const access = mapAccess(props.project, props.live, map.fragmentId, ws?.role);
  return (
    <div className="maps-page">
      <header className="page-head map-head">
        <h1 tabIndex={-1}>{map.title}</h1>
        <ReadOnlyNote note={access.note} />
      </header>
      <MapCanvas key={map.fragmentId} project={props.project} live={props.live} fragmentId={map.fragmentId} title={map.title} editable={access.editable} hrefFor={props.hrefFor} />
    </div>
  );
}

/** A map chosen in the contents (Research, or the Trash in Write): the same canvas in place of the editor. */
export function MapDocumentPage(props: PageProps & { fragmentId: string }) {
  const ws = useWorkspace();
  const title = props.node?.title || props.project.fragment(props.fragmentId)?.title || 'Mind Map';
  const access = mapAccess(props.project, props.live, props.fragmentId, ws?.role);
  return (
    <div className="maps-page">
      <header className="page-head map-head">
        <h1 tabIndex={-1}>{title}</h1>
        <ReadOnlyNote note={access.note} />
        <span className="page-actions">
          <ZoomControls fragmentId={props.fragmentId} />
          <PenControls disabled={!access.editable} />
        </span>
      </header>
      <MapCanvas key={props.fragmentId} project={props.project} live={props.live} fragmentId={props.fragmentId} title={title} editable={access.editable} hrefFor={props.hrefFor} />
    </div>
  );
}
